import ProgressBar from "@luminopia/react-toolbox/lib/progress_bar";
import {
  Table,
  TableCell,
  TableHead,
  TableRow,
} from "@luminopia/react-toolbox/lib/table";
import Search from "core/components/search";
import * as React from "react";
import { connect, Dispatch } from "react-redux";
import Card from "study_reviewer/components/Card";
import { IReviewer } from "../store/reviewers/model";
import {
  getTechniciansRequest,
  IState,
  selectTechnicians,
  inviteTechniciansRequest,
  removeTechnicianRequest,
} from "study_reviewer/store";
import { IconButton } from "core/components/button/icon-button";
import Confirm from "core/components/confirm";
import * as classNames from "./TechniciansListPage.css";
import { inviteTechniciansFormFactory } from "../components/technicians/invite-technicians-form";

export interface IReviewerRow {
  id: string;
  firstName: string;
  lastName: string;
  npi: string;
  email: string;
  isApproved: boolean;
  createdTime: string;
}
interface IStateProps {
  technicians: IReviewerRow[];
  isFetching: boolean;
}

interface IDispatchProps {
  onMount?: () => void;
  onInvite: ({
    reviewerId,
    emails,
  }: {
    reviewerId: string;
    emails: string;
  }) => void;
  onConfirmRemove: ({
    reviewerId,
    technicianId,
  }: {
    reviewerId: string;
    technicianId: string;
  }) => void;
}

interface IOwnProps {
  currentReviewer: IReviewer | null;
}

export interface IProps extends IStateProps, IDispatchProps, IOwnProps {}

interface IComponentState {
  filterValue: string;
  selectedTechnician: string | null;
  sortColumn: keyof IReviewerRow;
  sortDirection: "asc" | "desc";
}

const inviteTechsFormName = "inviteTechniciansForm";
const InviteTechniciansForm = inviteTechniciansFormFactory(inviteTechsFormName);

export class TechniciansListPage extends React.Component<
  IProps,
  IComponentState
> {
  public state: IComponentState = {
    filterValue: "",
    selectedTechnician: null,
    sortColumn: "createdTime",
    sortDirection: "desc",
  };

  private handleSort = (column: keyof IReviewerRow) => {
    this.setState((prevState) => ({
      sortColumn: column,
      sortDirection:
        prevState.sortColumn === column
          ? prevState.sortDirection === "asc"
            ? "desc"
            : "asc"
          : "asc",
    }));
  };

  private renderSortIcon = (column: keyof IReviewerRow) => {
    const { sortColumn, sortDirection } = this.state;
    if (column === sortColumn) {
      return (
        <i className={`material-icons ${classNames.sortIcon}`}>
          {sortDirection === "asc" ? "arrow_upward" : "arrow_downward"}
        </i>
      );
    }
    return null;
  };

  public render() {
    const { currentReviewer, isFetching } = this.props;

    if (isFetching) {
      return <ProgressBar type="circular" mode="indeterminate" multicolor />;
    }

    if (!currentReviewer) return null;

    return (
      <div>
        <Card>
          <InviteTechniciansForm
            onSubmit={this.props.onInvite}
            reviewerId={currentReviewer.id}
          />
        </Card>
        <Card>
          <Search
            value={this.state.filterValue}
            label="Search by First name, Last name or Email"
            onChange={this.handleChangeFilterValue}
            onClickClear={() => this.handleChangeFilterValue("")}
          />
          <Table selectable={false}>
            <TableHead>
              <TableCell
                className={classNames.sortableColumn}
                onClick={() => this.handleSort("firstName")}
              >
                First name {this.renderSortIcon("firstName")}
              </TableCell>
              <TableCell
                className={classNames.sortableColumn}
                onClick={() => this.handleSort("lastName")}
              >
                Last name {this.renderSortIcon("lastName")}
              </TableCell>
              <TableCell
                className={classNames.sortableColumn}
                onClick={() => this.handleSort("email")}
              >
                Email {this.renderSortIcon("email")}
              </TableCell>
              <TableCell>Remove</TableCell>
            </TableHead>
            {this.props.technicians
              .filter((technician) => {
                if (!technician) return false;
                const compareValue = this.state.filterValue.toUpperCase();
                return (
                  technician.firstName.toUpperCase().includes(compareValue) ||
                  technician.lastName.toUpperCase().includes(compareValue) ||
                  technician.email.toUpperCase().includes(compareValue)
                );
              })
              .sort((a, b) => {
                const { sortColumn, sortDirection } = this.state;
                const sortMultiplier = sortDirection === "asc" ? 1 : -1;
                let aVal = a[sortColumn];
                let bVal = b[sortColumn];
                if (typeof aVal === "string" && typeof bVal === "string") {
                  return sortMultiplier * aVal.localeCompare(bVal);
                }
                return 0;
              })
              .map((technician) => {
                return (
                  <TableRow
                    key={technician.npi}
                    className={
                      technician.isApproved ? null : classNames.isNotActive
                    }
                  >
                    <TableCell className={classNames.cell}>
                      {technician.firstName === "Unknown"
                        ? "-"
                        : technician.firstName}
                    </TableCell>
                    <TableCell className={classNames.cell}>
                      {technician.lastName === "Technician"
                        ? "-"
                        : technician.lastName}
                    </TableCell>
                    <TableCell className={classNames.cell}>
                      {technician.email}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        iconName="delete"
                        onClick={() =>
                          this.setState({ selectedTechnician: technician.id })
                        }
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
          </Table>
        </Card>
        <Confirm
          title="Remove technician?"
          active={this.state.selectedTechnician !== null}
          onConfirm={() => {
            const technicianId = this.state.selectedTechnician || "";
            this.props.onConfirmRemove({
              reviewerId: currentReviewer.id,
              technicianId,
            });
            this.setState({ selectedTechnician: null });
          }}
          onCancel={() => this.setState({ selectedTechnician: null })}
        >
          Are you sure you want to remove this technician? They will lose access
          to your patient data.
        </Confirm>
      </div>
    );
  }

  public componentDidMount() {
    if (this.props.onMount) this.props.onMount();
  }

  private handleChangeFilterValue = (value: string) => {
    this.setState({ filterValue: value });
  };
}

const mapStateToProps = (state: IState): IStateProps => {
  return {
    technicians: selectTechnicians(state),
    isFetching: state.technicians.isFetching,
  };
};

const mapDispatchToProps = (dispatch: Dispatch<void>, ownProps: IOwnProps) => ({
  onMount: () =>
    dispatch(
      getTechniciansRequest(
        (ownProps.currentReviewer && ownProps.currentReviewer.id) || ""
      )
    ),
  onInvite: ({ reviewerId, emails }: { reviewerId: string; emails: string }) =>
    dispatch(
      inviteTechniciansRequest(
        {
          reviewerId,
          emails: emails.split(","),
        },
        inviteTechsFormName
      )
    ),
  onConfirmRemove: ({
    reviewerId,
    technicianId,
  }: {
    reviewerId: string;
    technicianId: string;
  }) => dispatch(removeTechnicianRequest({ reviewerId, technicianId })),
});

export default connect<IStateProps, IDispatchProps, IOwnProps, IState>(
  mapStateToProps,
  mapDispatchToProps
)(TechniciansListPage);



// WEBPACK FOOTER //
// ./~/tslint-loader??ref--0!./study_reviewer/src/pages/TechniciansListPage.tsx